import { User, UserResponse } from "@/@types";
import { Endpoints, HttpService } from "@/api";
import { ESettingsType } from "@/enums/ESettingsType";
import ILogin from "@/interfaces/ILogin";
import { UserMapper } from "@/mappers";
import { useMainStore } from "@/store";
import { toggleModal } from "@/utils/Helper";
import moment from "moment";
import { unformat } from "v-money3";
import AppointmentService from "./AppointmentService";
import { paymentService } from "./index";

export interface UserServiceInterface {
  showUser: (id: number) => Promise<User>;
}

export default class UserService implements UserServiceInterface {
  constructor(private readonly http: HttpService) {}

  showUser = async (id: number): Promise<User> => {
    const { data } = await this.http.get<UserResponse>(
      `${Endpoints.users}/${id}`
    );

    if (!data) {
      throw new Error("Usuário não encontrado");
    }

    return UserMapper.toDomain(data);
  };

  static TOTAL_REGISTER_STEPS = 5;
  static GENDER_OPTIONS = [
    { id: "female", title: "Feminino" },
    { id: "male", title: "Masculino" },
    { id: "other", title: "Outro" },
  ];

  list = (params: any) => {
    return this.http.get(Endpoints.users, params);
  };
  prepareMoneyValue(value: string) {
    return unformat(value, {
      prefix: "R$ ",
      thousands: "",
      decimal: ",",
      precision: 2,
      disableNegative: true,
      minimumNumberOfCharacters: 1,
      modelModifiers: {
        number: true,
      },
    });
  }
  prepareParams(params: any) {
    if (params?.appointment_price) {
      params.appointment_price = this.prepareMoneyValue(
        params.appointment_price
      );
    }
    if (params?.first_appointment_price) {
      params.first_appointment_price = this.prepareMoneyValue(
        params.first_appointment_price
      );
    }
    if (params?.phone) {
      params.phone = params?.phone.clear();
    }

    params.account_link = true;
    params.web = true;

    return params;
  }
  create = async (params: any) => {
    const { saveToken, saveSession } = useMainStore();
    const values = this.prepareParams(params);
    const res = await this.http.post<any, any>(Endpoints.users, values, {
      encodeType: "formData",
    });
    if (res.access_token) {
      saveToken(res.access_token);
      const user = await this.showUser(res.data.id);
      saveSession(user);
    }
    return res;
  };
  show = (id: string | number) => {
    return this.http.get(`${Endpoints.users}/${id}`);
  };
  remove = () => {
    const store = useMainStore();
    const id = store.session?.id;
    return this.http.delete(`${Endpoints.users}/${id}`);
  };
  update = async (params: any) => {
    const { session, saveSession } = useMainStore();
    const id = session?.id;
    const values = this.prepareParams(params);
    const res = await this.http.put<any, any>(
      `${Endpoints.users}/${id}`,
      values
    );
    const user = await this.showUser(res.data.id);
    saveSession(user);
    return res;
  };
  updateProfileImage = (params: any) => {
    const store = useMainStore();
    const id = store.session?.id;
    return this.http.post(`${Endpoints.users}/${id}/edit`, params, {
      encodeType: "formData",
    });
  };
  login = async (params: ILogin) => {
    const { saveToken, saveSession } = useMainStore();
    const res = await this.http.post<any, any>(Endpoints.login, params);
    if (["psychologist"].includes(res.data.type)) {
      if (res.access_token) {
        saveToken(res.access_token);
        const user = await this.showUser(res.data.id);
        const { favorite_card } = await paymentService.getCreditCards();
        saveSession({ ...user, favorite_card });
      }
      return res;
    } else {
      throw { status: false, message: "Credenciais inválidas" };
    }
  };
  loginWithBearerToken = async (query: any) => {
    try {
      const { saveToken, saveSession } = useMainStore();
      saveToken(query.token);

      const user = await this.showUser(query.user_id);

      if (user.type === "psychologist") {
        saveSession(user);
        return true;
      }
      return false;
    } catch (error) {
      console.error("UserService.loginWithBearerToken", error);
      return false;
    }
  };
  updateMyBank = (params: any) => {
    return this.http.post(Endpoints.myBanks, params);
  };
  logout = () => {
    const store = useMainStore();
    store.clearSession();
    // store.commit(MUTATIONS.CLEAR_SESSION)
  };
  getTerms = () => {
    return this.http.get(`${Endpoints.termsOfUse}`);
  };
  getPrivacyPolicy = () => {
    return this.http.get(`${Endpoints.privacyPolicy}`);
  };
  getOccupationAreas = (params = {}) => {
    return this.http.get(Endpoints.occupationAreas, params);
  };
  getBanks = () => {
    return this.http.get(Endpoints.banks);
  };
  getAddressFromCep = async (cep: string) => {
    return fetch(`https://viacep.com.br/ws/${cep}/json`).then((r) => r.json());
  };
  updateStripePending = (params: object) => {
    return this.http.post(Endpoints.updateStripePending, params, {
      encodeType: "formData",
    });
  };
  getHomeInfo = () => {
    return this.http.get(Endpoints.homeInfo);
  };

  getAvatar = (userAvatar: string) => {
    var avatar = require("../assets/img/avatars/1.png");
    switch (String(userAvatar)) {
      case "1":
        avatar = require("../assets/img/avatars/1.png");
        break;
      case "2":
        avatar = require("../assets/img/avatars/2.png");
        break;
      case "3":
        avatar = require("../assets/img/avatars/3.png");
        break;
      case "4":
        avatar = require("../assets/img/avatars/4.png");
        break;
      case "5":
        avatar = require("../assets/img/avatars/5.png");
        break;
      case "6":
        avatar = require("../assets/img/avatars/6.png");
        break;
      case "7":
        avatar = require("../assets/img/avatars/7.png");
        break;
      case "8":
        avatar = require("../assets/img/avatars/8.png");
        break;
      case "9":
        avatar = require("../assets/img/avatars/9.png");
        break;
      case "10":
        avatar = require("../assets/img/avatars/10.png");
        break;
      case "11":
        avatar = require("../assets/img/avatars/11.png");
        break;
      case "12":
        avatar = require("../assets/img/avatars/12.png");
        break;
      case "13":
        avatar = require("../assets/img/avatars/13.png");
        break;
      case "14":
        avatar = require("../assets/img/avatars/14.png");
        break;
      case "15":
        avatar = require("../assets/img/avatars/15.png");
        break;
      case "16":
        avatar = require("../assets/img/avatars/16.png");
        break;
      case "17":
        avatar = require("../assets/img/avatars/17.png");
        break;
      case "18":
        avatar = require("../assets/img/avatars/18.png");
        break;
      case "19":
        avatar = require("../assets/img/avatars/19.png");
        break;
      case "20":
        avatar = require("../assets/img/avatars/20.png");
        break;
      case "21":
        avatar = require("../assets/img/avatars/21.png");
        break;
    }
    return avatar;
  };

  updateAvailabilitySettings = async (type: ESettingsType) => {
    const store = useMainStore();
    const userSession = store.session;
    let settingsLoading = {
      block_agenda: false,
      online: false,
      first_appointment: false,
      partnerships: false,
    };
    switch (type) {
      case ESettingsType.first_appointment:
        settingsLoading.first_appointment = true;
        break;
      case ESettingsType.block_agenda:
        settingsLoading.block_agenda = true;
        break;
      case ESettingsType.online:
        let res = await new AppointmentService().list({
          type: "scheduled",
          status: "aguardando",
        });
        if (res.data.length && res.data[0].online && userSession?.online) {
          toggleModal("agenda_warning");
          document.getElementById(type)?.click();
          return;
        }
        settingsLoading.online = true;
        break;
      case ESettingsType.partnerships:
        settingsLoading.partnerships = true;
        break;
    }

    let params: Partial<User> = {};

    if (userSession) {
      params[type] = !userSession[type];
    }

    const res = {
      params,
      settingsLoading,
    };

    return res;
  };
  calculateObjPercentage(obj: any) {
    try {
      if (obj) {
        const values = Object.values(obj);
        const concludedCount = values.filter((value) => value === true).length;
        const totalCount = values.length;
        const percentage = (concludedCount / totalCount) * 100;
        return {
          percentage,
          concludedCount,
          totalCount,
        };
      }
    } catch (error) {
      console.log(error);
    }
  }

  languages = () => {
    return this.http.get(Endpoints.languages);
  };

  getApproaches = () => {
    return this.http.get(Endpoints.approaches);
  };

  availableDays = () => {
    const session = useMainStore().session;
    return this.http.get(Endpoints.availableDays, { user_id: session?.id });
  };

  blockDate = (params: any) => {
    return this.http.post(`${Endpoints.specificDaySchedules}`, params);
  };

  getSpecificDaySchedules = (date?: string) => {
    return this.http.get(`${Endpoints.specificDaySchedules}`, { date });
  };

  removeSpecificDaySchedule = (id: number) => {
    return this.http.delete(`${Endpoints.specificDaySchedules}/${id}`);
  };

  availableTimes = async (
    date: string,
    user_id = useMainStore().session?.id
  ) => {
    const now = moment();
    const res = await this.http.get<any>(Endpoints.availableTimes, {
      user_id,
      date,
    });
    let times = res.data
      .filter((t: any) => moment(t).isAfter(now))
      .map((r: any) => {
        const st = moment(r);
        return {
          date: r,
          formattedDate: `${st.format(`dddd`)} das ${st.format(
            `HH[h]mm`
          )} às ${st.add(50, "minutes").format(`HH[h]mm`)}`.capitalizeFirst(),
        };
      });

    if (useMainStore().session?.online) {
      times = [
        {
          date: moment().format("YYYY-MM-DD HH:mm:ss"),
          formattedDate: "Agendar para agora!",
          online: true,
        },
        ...times,
      ];
    }
    return {
      status: true,
      data: times,
    };
  };

  getStripeAccountLink = (id: number) => {
    return this.http.get(`${Endpoints.stripeConnectOnboarding}/${id}`);
  };

  forgotPassword = (params: { email: string }) => {
    return this.http.post(Endpoints.forgotPassword, params);
  };

  resetPassword = (params: {
    email: string;
    password: string;
    token: string;
  }) => {
    return this.http.post(Endpoints.resetPassword, params);
  };

  stripeConnectReset = (id: number) => {
    return this.http.post(`${Endpoints.stripeConnectReset}/${id}`, {});
  };

  verifyRegisterAvailability = () => {
    return this.http.get(Endpoints.verifyRegisterAvailability);
  };
}
